import MediaService from '../../../../../Services/MediaService';
import searchProducts from '../../../../../Helpers/article/productSearch';

import {
    createActionButton,
    createAltInput,
    createCaptionElement,
    createContentContainer,
    createControlsContainer,
    createEmptyResultElement,
    createFileInput,
    createLicenseElement,
    createMetaContainer,
    createMetaField,
    createProductResultItem,
    createProductSearchContainer,
    createProductSearchInput,
    createProductTitleElement,
    createWrapper,
    findOrCreateImageElement,
    removeNode,
} from './Dom';

import { createCaptionState, normaliseSavedCaption } from './CaptionState';
import messageContext from '../../../../Context/MessageContext';
import slugifyAltText from '../../../../../Helpers/article/slugify';

class ImageTool {
    static get toolbox() {
        return {
            title: 'Image',
            icon: '<svg width="17" height="15" viewBox="0 0 336 276" xmlns="http://www.w3.org/2000/svg"><path d="M291 150V79c0-19-15-34-34-34H79c-19 0-34 15-34 34v42l67-44 81 72 56-29 42 30zm0 52l-43-30-56 30-81-67-66 39v23c0 19 15 34 34 34h178c17 0 31-13 34-29zM79 0h178c44 0 79 35 79 79v118c0 44-35 79-79 79H79c-44 0-79-35-79-79V79C0 35 35 0 79 0z"/></svg>',
        };
    }

    static get sanitize() {
        return {
            caption: {
                br: true,
                a: {
                    href: true,
                    target: true,
                    rel: true,
                },
            },
        };
    }

    constructor({ data, config, api }) {
        this.api = api;
        this.config = config;

        this.data = {
            path: data.path || null,
            caption: data.caption || null,
            license: data.license || null,
            alt: data.alt || null,
            product: data.product || null,
        };

        this.wrapper = null;
        this.controlsEl = null;
        this.contentEl = null;
        this.captionText = null;
        this.licenseText = null;
        this.altInput = null;
        this.fileInput = null;
        this.productResultsEl = null;
        this.productTitleEl = null;
        this.searchDebounce = null;
        this.onClickOutside = null;

        this.captionDefaultText = this.api?.i18n?.t('Caption') || 'Caption';
        this.licenseDefaultText = 'Image licence:';
        this.altDefaultText = 'Alt text';

        this.captionState = createCaptionState({
            defaultText: this.captionDefaultText,
            savedCaption: this.data.caption,
        });

        this.pushMessage = messageContext.pushMessage;
    }

    render() {
        this.wrapper = createWrapper();

        if (this.data.path || this.data.product) {
            this.ensureContent();
            this.renderImagePreview();
            this.renderProductTitle();
            this.renderMeta();
        }

        this.renderControls();

        return this.wrapper;
    }

    renderControls() {
        this.controlsEl = createControlsContainer();

        this.renderFileInput();
        this.renderUploadButton();
        this.renderProductButton();

        this.wrapper.appendChild(this.controlsEl);
    }

    renderFileInput() {
        this.fileInput = createFileInput();

        this.fileInput.addEventListener('change', async (event) => {
            const [file] = event.target.files || [];

            if (!file) {
                return;
            }

            await this.uploadImage(file);
            event.target.value = '';
        });

        this.controlsEl.appendChild(this.fileInput);
    }

    renderUploadButton() {
        const button = createActionButton('Click to upload image');

        button.addEventListener('click', () => {
            this.fileInput?.click();
        });

        this.controlsEl.appendChild(button);
    }

    renderProductButton() {
        const button = createActionButton(
            'Click to link a product',
            'idx-image__product-button',
        );

        button.addEventListener('click', () => {
            this.showProductSearch();
        });

        this.controlsEl.appendChild(button);
    }

    ensureContent() {
        if (this.contentEl) {
            return;
        }

        this.contentEl = createContentContainer();

        if (this.controlsEl) {
            this.wrapper.insertBefore(this.contentEl, this.controlsEl);
            return;
        }

        this.wrapper.appendChild(this.contentEl);
    }

    renderImagePreview() {
        if (!this.data.path) {
            return;
        }

        this.ensureContent();

        const img = findOrCreateImageElement(this.contentEl);
        img.src = ImageTool.buildImageUrl(this.data.path);
    }

    renderProductTitle() {
        if (!this.data.product) {
            if (this.productTitleEl) {
                removeNode(this.productTitleEl);
                this.productTitleEl = null;
            }
            return;
        }

        this.ensureContent();

        if (this.productTitleEl) {
            removeNode(this.productTitleEl);
        }

        this.productTitleEl = createProductTitleElement(this.data.product);
        this.contentEl.insertBefore(
            this.productTitleEl,
            this.contentEl.firstChild,
        );
    }

    renderMeta() {
        this.ensureContent();

        if (this.captionText && this.licenseText && this.altInput) {
            return;
        }

        const meta = createMetaContainer();

        this.captionText = createCaptionElement({
            defaultText: this.captionDefaultText,
            initialContent: this.data.caption || '',
        });

        this.altInput = createAltInput({
            defaultText: this.altDefaultText,
            initialContent: this.data.alt || '',
        });

        this.licenseText = createLicenseElement({
            defaultText: this.licenseDefaultText,
            initialContent: this.data.license || '',
        });

        this.captionText.addEventListener('input', this.handleCaptionInput);
        this.captionText.addEventListener('blur', this.handleCaptionBlur);
        this.altInput.addEventListener('blur', this.handleAltBlur);
        this.licenseText.addEventListener('blur', this.handleLicenseBlur);

        meta.appendChild(createMetaField('Description', this.captionText));
        meta.appendChild(createMetaField('Alt text', this.altInput));
        meta.appendChild(createMetaField('Licence', this.licenseText));

        this.contentEl.appendChild(meta);

        requestAnimationFrame(() => {
            this.captionText?.blur();
            this.altInput?.blur();
            this.licenseText?.blur();
        });
    }

    static clearIfEmpty(element) {
        if (!element) {
            return;
        }

        const text = element.textContent.trim();
        const html = element.innerHTML.trim();

        if (!text || html === '<br>' || html === '<div><br></div>') {
            element.innerHTML = '';
        }
    }

    static trimText(element) {
        if (!element) {
            return;
        }

        element.textContent = element.textContent.trim();
    }

    resetCaretAfterClear = () => {
        if (!this.captionText) return;

        this.captionText.focus();

        const selection = window.getSelection();
        if (!selection) return;

        const range = document.createRange();
        range.selectNodeContents(this.captionText);
        range.collapse(true);

        selection.removeAllRanges();
        selection.addRange(range);
    };

    handleCaptionInput = () => {
        if (!this.captionText) return;

        if (!this.captionText.textContent.trim()) {
            this.captionText.innerHTML = '';
            this.resetCaretAfterClear();
        }
    };

    handleCaptionBlur = () => {
        if (!this.captionText?.textContent.trim()) {
            this.captionText.innerHTML = '';
        }
    };

    handleAltBlur = () => {
        if (!this.altInput) {
            return;
        }
        this.altInput.value = slugifyAltText(this.altInput.value);
    };

    handleLicenseBlur = () => {
        ImageTool.trimText(this.licenseText);
        ImageTool.clearIfEmpty(this.licenseText);
    };

    async uploadImage(file) {
        try {
            const { data } = await MediaService.upload(
                file,
                this.config.resource,
            );

            this.data.path = data.path;

            this.ensureContent();
            this.renderImagePreview();
            this.renderMeta();
        } catch (error) {
            this.pushMessage('Image upload failed:', error);
        }
    }

    showProductSearch() {
        this.closeProductSearch();

        this.productResultsEl = createProductSearchContainer();
        const input = createProductSearchInput('Search product...');

        this.productResultsEl.appendChild(input);
        this.wrapper.appendChild(this.productResultsEl);

        this.onClickOutside = (event) => {
            if (
                this.productResultsEl &&
                !this.productResultsEl.contains(event.target)
            ) {
                this.closeProductSearch();
            }
        };

        document.addEventListener('mousedown', this.onClickOutside);

        input.addEventListener('input', (event) => {
            clearTimeout(this.searchDebounce);

            this.searchDebounce = setTimeout(async () => {
                await this.handleProductSearch(event.target.value, input);
            }, 300);
        });

        input.focus();
    }

    async handleProductSearch(term, inputEl) {
        if (!this.productResultsEl) {
            return;
        }

        try {
            const results = await searchProducts(term);

            Array.from(this.productResultsEl.children)
                .filter((child) => child !== inputEl)
                .forEach((child) => child.remove());

            if (!results.length) {
                this.productResultsEl.appendChild(
                    createEmptyResultElement('No results'),
                );
                return;
            }

            results.forEach((product) => {
                const item = createProductResultItem(product.name);

                item.addEventListener('mousedown', (event) => {
                    event.preventDefault();
                    event.stopPropagation();

                    this.selectProduct(product);
                    this.closeProductSearch();
                });

                this.productResultsEl.appendChild(item);
            });
        } catch (error) {
            this.pushMessage('Product search failed:', error);

            this.productResultsEl.innerHTML = '';
            this.productResultsEl.appendChild(inputEl);
            this.productResultsEl.appendChild(
                createEmptyResultElement('Search failed'),
            );
        }
    }

    selectProduct(product) {
        this.data.product = {
            href: `/kremek/${product.slug}`,
            name: product.name,
        };

        if (product.images?.[0]?.path) {
            this.data.path = product.images[0].path;
        }

        this.ensureContent();
        this.renderImagePreview();
        this.renderProductTitle();
        this.renderMeta();
    }

    closeProductSearch() {
        clearTimeout(this.searchDebounce);

        if (this.onClickOutside) {
            document.removeEventListener('mousedown', this.onClickOutside);
            this.onClickOutside = null;
        }

        if (this.productResultsEl) {
            removeNode(this.productResultsEl);
            this.productResultsEl = null;
        }
    }

    static buildImageUrl(path) {
        return `${import.meta.env.VITE_IMAGE_URL}/${path}`;
    }

    static extractPathFromImageSrc(src) {
        return src.replace(`${import.meta.env.VITE_IMAGE_URL}/`, '');
    }

    static applyLinkTargets(html) {
        if (!html) {
            return null;
        }

        const div = document.createElement('div');
        div.innerHTML = html;

        div.querySelectorAll('a').forEach((anchor) => {
            anchor.setAttribute('target', '_blank');
            anchor.setAttribute('rel', 'noopener noreferrer nofollow');
        });

        return div.innerHTML.trim() || null;
    }

    save(blockContent) {
        const image = blockContent.querySelector('img');
        const path = image
            ? ImageTool.extractPathFromImageSrc(image.src)
            : this.data.path;

        const rawCaption = this.captionText
            ? this.captionText.innerHTML
            : this.data.caption;

        const caption = normaliseSavedCaption({
            rawCaption,
            defaultText: this.captionState.defaultText,
            applyLinkTargets: (html) => ImageTool.applyLinkTargets(html),
        });

        const alt =
            slugifyAltText(this.altInput?.value || this.data.alt || '') || null;
        const license = this.licenseText?.textContent?.trim() || null;

        return {
            path,
            caption,
            alt,
            license,
            product: this.data.product || null,
        };
    }

    destroy() {
        this.closeProductSearch();

        if (this.captionText) {
            this.captionText.removeEventListener(
                'input',
                this.handleCaptionInput,
            );
            this.captionText.removeEventListener(
                'blur',
                this.handleCaptionBlur,
            );
        }

        if (this.altInput) {
            this.altInput.removeEventListener('blur', this.handleAltBlur);
        }

        if (this.licenseText) {
            this.licenseText.removeEventListener(
                'blur',
                this.handleLicenseBlur,
            );
        }
    }
}

export default ImageTool;
